import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Bug, Sparkles, ListTodo, Filter, Search, AlertCircle, Layers, User as UserIcon, Kanban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { fetchSolicitudes, fetchEstadisticas } from '@/services/solicitudesDesarrollo.service';
import { fetchUsersRol } from '@/services/users.service';
import { useAuth } from '@/contexts/AuthContext';
import SolicitudFormModal from './components/SolicitudFormModal';
import SolicitudDetailModal from './components/SolicitudDetailModal';
import BacklogView from './components/BacklogView';
import MisTareasView from './components/MisTareasView';
import TableroView from './components/TableroView';

const SolicitudesDesarrolloPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  
  const [activeTab, setActiveTab] = useState('backlog');
  const [filters, setFilters] = useState({
    tipo: '',
    estado: 'pendiente',
    prioridad: '',
    busqueda: '',
    asignadoA: '',
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });

  const [tableroFilters, setTableroFilters] = useState({
    asignadoA: user?.id || '',
    estado: '',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [filters, pagination.page, tableroFilters]);

  useEffect(() => {
    // Inicializar filtro de tablero con usuario actual cuando se carga el componente
    if (user?.id && !tableroFilters.asignadoA) {
      setTableroFilters({ asignadoA: user.id });
    }
  }, [user]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadData();
  }, [activeTab]);

  const loadUsers = async () => {
    try {
      const response = await fetchUsersRol({ limit: 100, offset: 1 });
      const usersData = Array.isArray(response) ? response : response?.data || [];
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Determinar qué filtros aplicar según el tab activo
      let appliedFilters = {};
      
      if (activeTab === 'backlog') {
        // En backlog se usan los filtros generales
        appliedFilters = { ...filters, page: pagination.page, limit: pagination.limit };
      } else if (activeTab === 'mis-tareas') {
        // En mis-tareas solo se filtran las tareas del usuario actual
        appliedFilters = { asignadoAId: user?.id, page: pagination.page, limit: pagination.limit };
      } else if (activeTab === 'tablero') {
        // En tablero se usa el filtro específico de tablero
        appliedFilters = { 
          ...(tableroFilters.asignadoA && tableroFilters.asignadoA !== 'all' ? { asignadoAId: tableroFilters.asignadoA } : {}),
          page: pagination.page, 
          limit: pagination.limit 
        };
      }
      
      const [solicitudesResponse, estadisticasData] = await Promise.all([
        fetchSolicitudes(appliedFilters),
        fetchEstadisticas(),
      ]);
      
      // Manejar respuesta paginada o array directo (retrocompatibilidad)
      if (solicitudesResponse.data) {
        setSolicitudes(solicitudesResponse.data);
        setPagination(prev => ({
          ...prev,
          total: solicitudesResponse.total,
          page: solicitudesResponse.page,
          limit: solicitudesResponse.limit,
        }));
      } else {
        setSolicitudes(solicitudesResponse);
      }
      
      setEstadisticas(estadisticasData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    setIsFormModalOpen(false);
    loadData();
    toast({
      title: 'Solicitud creada',
      description: 'La solicitud se ha creado exitosamente',
    });
  };

  const handleViewDetail = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsDetailModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    setIsDetailModalOpen(false);
    setSelectedSolicitud(null);
    loadData();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'error':
        return <Bug className="h-4 w-4" />;
      case 'sugerencia':
        return <Sparkles className="h-4 w-4" />;
      case 'tarea':
        return <ListTodo className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'error':
        return 'Error';
      case 'sugerencia':
        return 'Sugerencia';
      case 'tarea':
        return 'Tarea';
      default:
        return tipo;
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'critica':
        return 'destructive';
      case 'alta':
        return 'default';
      case 'media':
        return 'secondary';
      case 'baja':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'en_progreso':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completado':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cerrado':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'cancelado':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'Pendiente';
      case 'en_progreso':
        return 'En Progreso';
      case 'completado':
        return 'Completado';
      case 'cerrado':
        return 'Cerrado';
      case 'cancelado':
        return 'Cancelado';
      default:
        return estado;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Tareas </h1>
          <p className="text-muted-foreground">Gestiona errores, sugerencias y tareas pendientes</p>
        </div>
        <Button onClick={() => setIsFormModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Solicitud
        </Button>
      </div>

 {/*      {estadisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{estadisticas.total || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pendientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {estadisticas.porEstado?.pendiente || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">En Progreso</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {estadisticas.porEstado?.en_progreso || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Completados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {estadisticas.porEstado?.completado || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )} */}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-3">
          <TabsTrigger value="backlog" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Backlog
          </TabsTrigger>
          <TabsTrigger value="mis-tareas" className="flex items-center gap-2">
            <UserIcon className="h-4 w-4" />
            Mis Tareas
          </TabsTrigger>
          <TabsTrigger value="tablero" className="flex items-center gap-2">
            <Kanban className="h-4 w-4" />
            Tablero
          </TabsTrigger>
        </TabsList>

        {activeTab === 'backlog' && (
          <Card>
            <CardHeader>
              <CardTitle>Filtros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Búsqueda</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por título o descripción..."
                      value={filters.busqueda}
                      onChange={(e) => setFilters({ ...filters, busqueda: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Tipo de solicitud</label>
                  <Select
                    value={filters.tipo || "all"}
                    onValueChange={(value) => setFilters({ ...filters, tipo: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los tipos</SelectItem>
                      <SelectItem value="error">🐛 Error</SelectItem>
                      <SelectItem value="sugerencia">✨ Sugerencia</SelectItem>
                      <SelectItem value="tarea">📋 Tarea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Estado</label>
                  <Select
                    value={filters.estado || "all"}
                    onValueChange={(value) => setFilters({ ...filters, estado: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      <SelectItem value="pendiente">⏳ Pendiente</SelectItem>
                      <SelectItem value="en_progreso">🔄 En Progreso</SelectItem>
                      <SelectItem value="completado">✅ Completado</SelectItem>
                      <SelectItem value="cerrado">🔒 Cerrado</SelectItem>
                      <SelectItem value="cancelado">❌ Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Prioridad</label>
                  <Select
                    value={filters.prioridad || "all"}
                    onValueChange={(value) => setFilters({ ...filters, prioridad: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar prioridad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las prioridades</SelectItem>
                      <SelectItem value="critica">🔴 Crítica</SelectItem>
                      <SelectItem value="alta">🟠 Alta</SelectItem>
                      <SelectItem value="media">🟡 Media</SelectItem>
                      <SelectItem value="baja">🟢 Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Asignado a</label>
                  <Select
                    value={filters.asignadoA || "all"}
                    onValueChange={(value) => setFilters({ ...filters, asignadoA: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar usuario" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los usuarios</SelectItem>
                      <SelectItem value="unassigned">👤 Sin asignar</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u._id} value={u._id}>
                          {u.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'tablero' && (
          <Card>
            <CardHeader>
              <CardTitle>Filtros del Tablero</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Filtrar por usuario</label>
                  <Select
                    value={tableroFilters.asignadoA || "all"}
                    onValueChange={(value) => setTableroFilters({ asignadoA: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar usuario" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los usuarios</SelectItem>
                      <SelectItem value={user?.id}>👤 Mis tareas</SelectItem>
                      {users.filter(u => u._id !== user?.id).map((u) => (
                        <SelectItem key={u._id} value={u._id}>
                          {u.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <TabsContent value="backlog" className="mt-0">
          <BacklogView
            solicitudes={solicitudes}
            loading={loading}
            onViewDetail={handleViewDetail}
            getTipoIcon={getTipoIcon}
            getTipoLabel={getTipoLabel}
            getPrioridadColor={getPrioridadColor}
            getEstadoColor={getEstadoColor}
            getEstadoLabel={getEstadoLabel}
          />
        </TabsContent>

        <TabsContent value="mis-tareas" className="mt-0">
          <MisTareasView
            solicitudes={solicitudes}
            loading={loading}
            currentUserId={user?.id}
            onViewDetail={handleViewDetail}
            getTipoIcon={getTipoIcon}
            getTipoLabel={getTipoLabel}
            getPrioridadColor={getPrioridadColor}
            getEstadoColor={getEstadoColor}
            getEstadoLabel={getEstadoLabel}
          />
        </TabsContent>

        <TabsContent value="tablero" className="mt-0">
          <TableroView
            solicitudes={solicitudes}
            loading={loading}
            onViewDetail={handleViewDetail}
            getTipoIcon={getTipoIcon}
            getTipoLabel={getTipoLabel}
            getPrioridadColor={getPrioridadColor}
          />
        </TabsContent>
      </Tabs>

      {/* Paginación */}
      {!loading && totalPages > 1 && (
        <Card className="mt-4">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} solicitudes
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Anterior
                </Button>
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = idx + 1;
                    } else if (pagination.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = pagination.page - 2 + idx;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pagination.page === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <SolicitudFormModal
        open={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSuccess={handleCreateSuccess}
        user={user}
      />

      {selectedSolicitud && (
        <SolicitudDetailModal
          open={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedSolicitud(null);
          }}
          solicitud={selectedSolicitud}
          onUpdate={handleUpdateSuccess}
          user={user}
        />
      )}
    </div>
  );
};

export default SolicitudesDesarrolloPage;
